import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatAccordion } from '@angular/material/expansion';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import {
  Calendar,
  DateRangeType,
  durationStringToMinutes,
  EventTypeDateRangeInterface,
  FormFieldType,
  HostUser,
  MeetingLocationType,
  MeetingType,
  MeetingTypeFormField,
  Preferences,
  TeamEventMeetingType,
  RelativeTimeUnit,
  NotificationType
} from '@vendasta/meetings';
import camelCase from 'lodash/camelCase';
import moment from 'moment-timezone';
import { combineLatest, from, Observable, Subscription, BehaviorSubject } from 'rxjs';
import { first, map, shareReplay, startWith, switchMap, take, tap, finalize, skip } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { MeetingSchedulerStoreService } from '../data-providers/meeting-scheduler-store.service';
import {
  FEATURE_MEETING_DATE_RANGE_TOKEN,
  HOST_ID_TOKEN,
  MEETING_LIST_PAGE_TOKEN,
  MEETING_TYPE_LIST_VIEW_PAGE_TOKEN,
  FEATURE_PRIORITY_ASSIGNMENT_TOKEN,
  EVENT_TYPE_PAGE_TOKEN,
  FEATURE_SMS_ENABLED_TOKEN,
} from '../data-providers/providers';
import { UpdateBookingLinkConfirmationDialogComponent } from './update-booking-link-confirmation-dialog/update-booking-link-confirmation-dialog.component';
import { BookingUrlDialogComponent } from '../meeting-list/booking-url-dialog/booking-url-dialog.component';
import { DailyAvailability } from '../interface';
import { ScheduleSettingsViewService } from '../schedule-settings/schedule-settings-view.service';
import { AccountGroupService, ProjectionFilter } from '@galaxy/account-group';
import { BusinessNavConfigService } from '@vendasta/business-nav';
import { WhitelabelService } from '@galaxy/partner';
import { EVENTS, POSTHOG_ACTIONS, POSTHOG_CATEGORIES, POSTHOG_KEYS } from '../constants';
import { MatChipGrid, MatChipInputEvent } from '@angular/material/chips';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { AvailabilityRuleList } from '@vendasta/meetings/lib/_internal/objects/meeting-host';
import { getCommonTimeSlots } from '../utils';
import { RegistrationCutOffUnit } from '@vendasta/meetings';
import { CalendarConfig } from '../shared/components/calendar-date-picker/calendar-date-picker.component';

interface DurationOption {
  seconds: number;
  label: string;
}

@Component({
  selector: 'meeting-scheduler-booking-link',
  templateUrl: './booking-link-page.component.html',
  styleUrls: ['./booking-link-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BookingLinkPageComponent implements OnInit, OnDestroy {
  hostPreferences: Preferences;
  address: string;
  canSendSms = false; // Will be loaded from SMS SDK v2 API
  NotificationType = NotificationType; // Expose enum to template

  buttonText: string;
  linkPrefix$: Observable<string>;
  readonly customDuration = 'custom';
  readonly durations = ['15', '30', '45', '60'];
  readonly increments: DurationOption[] = [
    { seconds: 900, label: '15' },
    { seconds: 1800, label: '30' },
    { seconds: 3600, label: '60' },
  ];
  readonly buffers: DurationOption[] = [
    { seconds: 0, label: '0' },
    { seconds: 300, label: '5' },
    { seconds: 600, label: '10' },
    { seconds: 900, label: '15' },
    { seconds: 1800, label: '30' },
    { seconds: 3600, label: '60' },
  ];
  hourTime = 3600;
  readonly advanceNotice: DurationOption[] = [
    { seconds: 0, label: '0' },
    { seconds: this.hourTime, label: `1` },
    { seconds: this.hourTime * 2, label: `2` },
    { seconds: this.hourTime * 4, label: `4` },
    { seconds: this.hourTime * 12, label: `12` },
    { seconds: this.hourTime * 24, label: `24` },
    { seconds: this.hourTime * 48, label: `48` },
  ];

  teamMember: UntypedFormGroup;
  teamMemberCtrl = new FormControl('', [this.teamMembersConfiguredValidator()]);
  selectedTeamMembers: HostUser[] = [];
  private selectedTeamMembersSubject = new BehaviorSubject<HostUser[]>([]);
  filteredTeamMembers: Observable<HostUser[]>;
  allTeamMembers: any[] = [];
  separatorKeysCodes = [13, 188]; // Enter and comma as separators.
  isUpdate = false;
  private loadingSubject = new BehaviorSubject<boolean>(false);
  loading$: Observable<boolean> = this.loadingSubject.asObservable();
  showHostUserSection$: Observable<boolean>;
  isSessionEventType$: Observable<boolean>;
  private meetingTypes$: Observable<MeetingType[]>;
  private meetingTypeToUpdate$: Observable<MeetingType>;
  meetingId;
  timezone;
  meetingIdNotNull = false;
  savingAvailabilityForm = false;
  private initialLoadComplete = false;
  private formInitialized = false; // Flag to track if form has been initialized from API
  isSessionEvent = false;

  calendar: Calendar | null = null;

  get teamMemberListArray(): UntypedFormArray {
    return this.teamMember.get('teamMemberName') as UntypedFormArray;
  }

  private originalSlug: string;
  private usedSlugs: string[] = [];
  private readonly slugRandomness = uuidv4().split('-')[0];

  readonly colors = [
    '#EE5353',
    '#F778B4',
    '#E27EFF',
    '#8988FC',
    '#4A91E9',
    '#0BC0D7',
    '#34C66F',
    '#67C820',
    '#DFC12C',
    '#F49A31',
  ];

  fieldTypes = new Map<FormFieldType, string>([
    [
      FormFieldType.FORM_FIELD_TYPE_TEXT,
      this.translate.instant('MEETING_SCHEDULER.BOOKING_LINK.QUESTIONS_SECTION.TYPE_TEXT'),
    ],
    [
      FormFieldType.FORM_FIELD_TYPE_EMAIL,
      this.translate.instant('MEETING_SCHEDULER.BOOKING_LINK.QUESTIONS_SECTION.TYPE_EMAIL'),
    ],
    [
      FormFieldType.FORM_FIELD_TYPE_PHONE_NUMBER,
      this.translate.instant('MEETING_SCHEDULER.BOOKING_LINK.QUESTIONS_SECTION.TYPE_PHONE_NUMBER'),
    ],
  ]);
  fieldTypeKeys = Array.from(this.fieldTypes.keys());
  fieldLabelMax = 50;
  hostUserIds: string[] = [];
  calendarId: string;
  meetingTypeId: string;
  hostid: string;
  applicationContext: any;
  businessId: string;
  partnerid: string;
  context: string;
  eventTypeName: string;
  isAvailabilitySelected: boolean;
  availabilitySelected: DailyAvailability;
  displayAvailabilitySelectionError: boolean;
  displayAvailabilitySelectionSlotError: boolean;
  displayAvailabilityInsufficientDurationError: boolean;
  meetingDuration: number;
  isMultiHostMeetingType: boolean;
  dateRangeConfig: EventTypeDateRangeInterface;

  // Properties for handling race condition between team members and priority order loading
  private pendingHostOrderDetails: any = null;
  private pendingMeetingType: TeamEventMeetingType | null = null;

  public groupCalendars$: Observable<Calendar[]>;
  public sessionCalendar$: Observable<Calendar>;
  isShowContent = false;
  showScheduleAccordion = false;
  eventTypePageLink: string;
  private shouldNavigateToSchedule = false;

  // Properties for schedule meeting dialog functionality
  calendarConfig: CalendarConfig | null = null;
  selectedDate: Date | null = null;
  availableTimeSlots: string[] = [];
  hostPreferencesLoaded = false;
  private readonly subscriptions: Subscription[] = [];
  showAlert$: Observable<boolean>;
  teamMembers$: Observable<HostUser[]>;
  hostAvailability$: Observable<Map<string, AvailabilityRuleList>>;
  @ViewChild('accordion') private readonly accordion: MatAccordion;
  @ViewChild('teamMemberChipGrid') teamMemberChipGrid: MatChipGrid;
  @ViewChild('teamMemberInput') teamMemberInput: ElementRef<HTMLInputElement>;
  @ViewChild('teamMemberSection', { static: false }) teamMemberSection: ElementRef;

  customFields = this.formBuilder.array([], this.noDuplicates());

  form = this.formBuilder.group({
    id: this.formBuilder.control(''),
    name: this.formBuilder.control('', [Validators.required]),
    sessionTitle: this.formBuilder.control(''),
    durationSelection: this.formBuilder.control('15', [Validators.required]),
    durationInput: this.formBuilder.control('15', [
      Validators.required,
      Validators.pattern('[0-9]+'),
      Validators.min(15),
    ]),
    description: this.formBuilder.control(''),
    color: this.formBuilder.control(this.colors[0]),
    meetingLocation: this.formBuilder.control('video'), // Default value
    radioSelection: this.formBuilder.control('userSite'),
    radioChoice: this.formBuilder.control(TeamEventMeetingType.ROUND_ROBIN), // Setting default value
    emailSubject: this.formBuilder.control(''),
    emailDescription: this.formBuilder.control(''),
    inPersonAddress: this.formBuilder.control(''),
    locationGuidelines: this.formBuilder.control(''),
    slug: this.formBuilder.control('', [Validators.required, Validators.pattern('[a-zA-Z0-9_-]+'), this.uniqueSlug()]),
    customFields: this.customFields,
    availabilityIncrement: this.formBuilder.control('-1'),
    bufferDurationAfterMeeting: this.formBuilder.control('-1'),
    bufferDurationBeforeMeeting: this.formBuilder.control('-1'),
    noticeTime: this.formBuilder.control('-1'),
    isVideoLinkRequired: this.formBuilder.control(true),
    enableEmailField: this.formBuilder.control({ value: true, disabled: !this.canSendSms }),
    enablePhoneNumberField: this.formBuilder.control({ value: false, disabled: !this.canSendSms }),
    notificationType: this.formBuilder.control(NotificationType.EMAIL), // EMAIL or SMS - used when both are required
    maxAttendees: this.formBuilder.control('', [Validators.min(1), Validators.max(100)]),
    registrationCutoffValue: this.formBuilder.control(0),
    registrationCutoffUnit: this.formBuilder.control(0), // Default to Minutes
    // Date range controls
    dateRangeType: this.formBuilder.control(DateRangeType.RELATIVE, [Validators.required]),
    relativeValue: this.formBuilder.control(4, [Validators.required, Validators.min(1), Validators.max(365)]),
    relativeUnit: this.formBuilder.control(RelativeTimeUnit.WEEKS, [Validators.required]),
    startDate: this.formBuilder.control(null),
    endDate: this.formBuilder.control(null),
    selectedTime: this.formBuilder.control(''),
  });

  // Date range properties
  DateRangeType = DateRangeType;
  RelativeTimeUnit = RelativeTimeUnit;

  timeUnits = [
    { value: RelativeTimeUnit.DAYS, label: 'Days' },
    { value: RelativeTimeUnit.WEEKS, label: 'Weeks' },
    { value: RelativeTimeUnit.MONTHS, label: 'Months' },
  ];

  cutoffUnits = [
    { value: RegistrationCutOffUnit.REGISTRATION_CUTOFF_MINUTES, label: 'Minute(s)' },
    { value: RegistrationCutOffUnit.REGISTRATION_CUTOFF_HOURS, label: 'Hour(s)' },
    { value: RegistrationCutOffUnit.REGISTRATION_CUTOFF_DAYS, label: 'Day(s)' },
  ];

  // Today's date for date picker minimum
  today = new Date();

  constructor(
    @Inject(MEETING_LIST_PAGE_TOKEN) public readonly meetingListLink$: Observable<string>,
    @Inject(MEETING_TYPE_LIST_VIEW_PAGE_TOKEN) public readonly meetingTypeListViewPage$: Observable<string>,
    @Inject(HOST_ID_TOKEN) private readonly _hostId$: Observable<string>,
    private accountGroupService: AccountGroupService,
    @Inject('PARTNER_ID') readonly partnerId$: Observable<string>,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly translate: TranslateService,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly storeService: MeetingSchedulerStoreService,
    private readonly alerts: SnackbarService,
    private readonly dialog: MatDialog,
    private service: BusinessNavConfigService,
    private readonly scheduleSettingsViewService: ScheduleSettingsViewService,
    private whitelabelService: WhitelabelService,
    private cdr: ChangeDetectorRef,
    private readonly analyticsService: ProductAnalyticsService,
    @Inject(FEATURE_MEETING_DATE_RANGE_TOKEN)
    readonly featureDateRangeEnabled$: Observable<boolean>,
    @Inject(FEATURE_PRIORITY_ASSIGNMENT_TOKEN)
    readonly featurePriorityAssignmentEnabled$: Observable<boolean>,
    @Inject(EVENT_TYPE_PAGE_TOKEN) public readonly eventTypePageLink$: Observable<string>,
    @Inject(FEATURE_SMS_ENABLED_TOKEN)
    readonly featureSMSEnabled$: Observable<boolean>,
  ) {}

  async ngOnInit() {
    this.storeService.selectedToggle = EVENTS;
    this.subscriptions.push(
      this._hostId$.subscribe((id) => {
        this.hostid = id;
      }),
      this.eventTypePageLink$.subscribe((value) => (this.eventTypePageLink = value)),
    );
    const inPersonAddressControl = this.inPersonAddressControl;
    const locationGuidelinesControl = this.locationGuidelinesControl;

    // Clear all validators first
    inPersonAddressControl?.clearValidators();
    locationGuidelinesControl?.clearValidators();

    this.groupCalendars$ = this.storeService.loadGroupCalendars();
    this.sessionCalendar$ = this.storeService.loadSessionCalendar();

    this.calendarId = this.route.snapshot.params.calendarId || '';

    // Check query parameters to determine if schedule accordion should be shown
    const queryParams = this.route.snapshot.queryParams;
    this.showScheduleAccordion =
      queryParams['show_schedule_accordion'] === 'true' && queryParams['event_type'] === 'Session';

    // Note: Calendar initialization moved to after meetingId is set

    await this.loadCalendar();

    // Set loading to true when we start loading data (especially for update mode)
    this.loadingSubject.next(true);

    const hostsResponse$ = this.storeService.loadHostsForCalendar(this.calendarId);

    // Extract team members from the response
    this.teamMembers$ = hostsResponse$.pipe(map((response) => response.hostUsers || []));

    // Extract host availability from the response
    this.hostAvailability$ = hostsResponse$.pipe(
      map((response) => response.hostAvailability || new Map<string, AvailabilityRuleList>()),
    );

    this.subscriptions.push(
      this.form.controls.radioChoice.valueChanges.subscribe((meetingType) => {
        if (meetingType === TeamEventMeetingType.MULTI_HOST) {
          this.isMultiHostMeetingType = true;
          if (this.form.controls.bufferDurationAfterMeeting.value === '-1') {
            this.form.controls.bufferDurationAfterMeeting.setValue(0);
          }
          if (this.form.controls.bufferDurationBeforeMeeting.value === '-1') {
            this.form.controls.bufferDurationBeforeMeeting.setValue(0);
          }
        } else {
          this.isMultiHostMeetingType = false;
        }
        // Validate availability when switching to multi-host
        this.validateAvailabilitySlots();
      }),
      this.teamMembers$
        .pipe(map((teamMembers) => teamMembers.filter(({ displayName }) => !!displayName)))
        .subscribe((teamMembers) => {
          this.allTeamMembers = teamMembers;
          this.filteredTeamMembers = this.teamMemberCtrl.valueChanges.pipe(
            startWith(''),
            map((value) => this._filter(value || '')),
          );
          if (this.hostUserIds && this.hostUserIds.length > 0) {
            // Preserve the existing order of selectedTeamMembers if already set
            if (this.selectedTeamMembers.length === 0) {
              this.selectedTeamMembers = this.allTeamMembers.filter((member) =>
                this.hostUserIds.includes(member.userId),
              );
              this.selectedTeamMembersSubject.next(this.selectedTeamMembers);
            } else {
              // Update the selectedTeamMembers array with new data while preserving order
              this.selectedTeamMembers = this.selectedTeamMembers.map((existingMember) => {
                const updatedMember = this.allTeamMembers.find((member) => member.userId === existingMember.userId);
                return updatedMember || existingMember;
              });
              this.selectedTeamMembersSubject.next(this.selectedTeamMembers);
            }

            // Check if we have pending hostOrderDetails to apply
            const pendingHostOrderDetails = this.pendingHostOrderDetails;
            const pendingMeetingType = this.pendingMeetingType;

            if (pendingHostOrderDetails && pendingMeetingType === TeamEventMeetingType.PRIORITY_ROUND_ROBIN) {
              this.applyHostOrderToSelectedMembers(pendingHostOrderDetails);

              // Clear pending data
              this.pendingHostOrderDetails = null;
              this.pendingMeetingType = null;
              this.cdr.detectChanges();
            }
          }
        }),
    );

    this.showHostUserSection$ = this.groupCalendars$.pipe(
      map((calendars) => Boolean((calendars || []).find((c) => c.id === this.calendarId))),
    );

    this.isSessionEventType$ = this.storeService.loadSessionCalendar().pipe(
      map((sessionCalendar) => {
        if (!sessionCalendar) {
          return false; // Not a session event type if no session calendar exists
        }
        const isSession = sessionCalendar.id === this.calendarId;
        return isSession;
      }),
      startWith(false),
    );

    const meetingTypeId = this.route.snapshot.params.eventTypeId || '';
    this.meetingTypeId = meetingTypeId;
    this.isUpdate = meetingTypeId !== '';
    this.cdr.markForCheck();
    this.linkPrefix$ = this.storeService.loadPersonalGeneralBookingLink().pipe(
      map((url) => {
        const parts = url.replace('https://', '').replace('http://', '').split('/');
        return (parts.length > 0 ? parts[0] : '') + '/you/';
      }),
    );

    // Load host preferences to get timezone
    this.subscriptions.push(
      this.storeService.loadUserPreferences().subscribe((preferences) => {
        this.hostPreferences = preferences;
        this.timezone = preferences?.timezone?.id || preferences?.timezone || 'America/New_York';
        this.hostPreferencesLoaded = true;
        // Reinitialize calendar if needed
        if (this.meetingId && (this.showScheduleAccordion || this.isSessionEventType$)) {
          this.initializeScheduleCalendar();
        }
      }),
    );

    this.subscriptions.push(
      this.form.controls.meetingLocation.valueChanges.subscribe((value) => {
        inPersonAddressControl?.clearValidators();
        locationGuidelinesControl?.clearValidators();
        if (value !== 'video') {
          if (this.radioSelectionControl.value === 'userSite') {
            inPersonAddressControl?.setValidators([Validators.required]);
          } else {
            locationGuidelinesControl?.setValidators([Validators.required]);
          }
        }
        inPersonAddressControl?.updateValueAndValidity();
        locationGuidelinesControl?.updateValueAndValidity();
      }),
      this.form.controls.isVideoLinkRequired.valueChanges.subscribe(),
      this.form.controls.radioSelection.valueChanges.subscribe((value) => {
        inPersonAddressControl?.clearValidators();
        locationGuidelinesControl?.clearValidators();
        if (this.meetingLocationControl.value !== 'video') {
          if (value === 'userSite') {
            inPersonAddressControl?.setValidators([Validators.required]);
          } else {
            locationGuidelinesControl?.setValidators([Validators.required]);
          }
        }
        inPersonAddressControl?.updateValueAndValidity();
        locationGuidelinesControl?.updateValueAndValidity();
      }),
    );

    this.subscriptions.push(
      this.form.controls.name.valueChanges.subscribe(() => {
        const controls = this.form.controls;
        const name = controls.name.value as string;
        this.eventTypeName = name;
        if (controls.name.touched) {
          this.slugControl?.markAsTouched();
        }
        if (!this.isUpdate && !controls.slug.dirty) {
          let generatedSlug = name
            .toLowerCase()
            .replace(/\s/g, '-')
            .replace(/[^0-9a-zA-Z_-]/g, '');
          if (this.usedSlugs.includes(generatedSlug)) {
            generatedSlug += '-' + this.slugRandomness;
          }
          controls.slug.setValue(generatedSlug);
          controls.slug.markAsPristine();
        }
      }),
    );
    this.subscriptions.push(
      this.form.controls.durationSelection.valueChanges.subscribe(() => {
        const controls = this.form.controls;
        const selection = controls.durationSelection.value;
        const input = controls.durationInput;
        if (selection !== this.customDuration && selection !== input.value) {
          input.setValue(selection);
        }
        // Validate availability when duration changes
        this.validateAvailabilitySlots();
      }),
    );

    this.subscriptions.push(
      this.form.controls.durationInput.valueChanges.subscribe(() => {
        // Validate availability when duration input changes
        this.validateAvailabilitySlots();
      }),
      this.form.controls.bufferDurationAfterMeeting.valueChanges.subscribe(() => {
        // Validate availability when buffer duration changes
        this.validateAvailabilitySlots();
      }),
      this.form.controls.bufferDurationBeforeMeeting.valueChanges.subscribe(() => {
        // Validate availability when buffer duration changes
        this.validateAvailabilitySlots();
      }),
      this.form.controls.dateRangeType.valueChanges.subscribe(() => {
        this.updateDateRangeValidators();
      }),
    );

    // Add conditional validation for session-specific fields
    this.subscriptions.push(
      this.isSessionEventType$.subscribe((isSession) => {
        const nameControl = this.nameControl;
        const sessionTitleControl = this.sessionTitleControl;
        const maxAttendeesControl = this.maxAttendeesControl;
        const slugControl = this.slugControl;
        const durationSelectionControl = this.durationSelectionControl;
        const durationInputControl = this.durationInputControl;

        // Set button text based on session type, update mode, and schedule flow
        if (isSession) {
          this.isSessionEvent = true;

          if (this.showScheduleAccordion) {
            this.buttonText = this.translate.instant('MEETING_SCHEDULER.BOOKING_LINK.SCHEDULE_SESSION');
          } else {
            this.buttonText = this.isUpdate
              ? this.translate.instant('MEETING_SCHEDULER.BOOKING_LINK.UPDATE_SESSION')
              : this.translate.instant('MEETING_SCHEDULER.BOOKING_LINK.CREATE_SESSION');
          }
        } else {
          this.buttonText = this.isUpdate
            ? this.translate.instant('MEETING_SCHEDULER.BOOKING_LINK.UPDATE')
            : this.translate.instant('MEETING_SCHEDULER.BOOKING_LINK.CREATE');
        }

        // Handle validation for schedule flow - name field should not be required
        if (this.showScheduleAccordion) {
          nameControl?.clearValidators();
          nameControl?.updateValueAndValidity();

          // Add required validators for scheduling flow
          sessionTitleControl?.setValidators([Validators.required]);
          maxAttendeesControl?.setValidators([Validators.required, Validators.min(1), Validators.max(100)]);
          this.registrationCutoffValueControl?.setValidators([Validators.required, Validators.min(0)]);
          this.selectedTimeControl?.setValidators([Validators.required]);

          // Update validity for scheduling-specific fields
          sessionTitleControl?.updateValueAndValidity();
          maxAttendeesControl?.updateValueAndValidity();
          this.registrationCutoffValueControl?.updateValueAndValidity();
          this.selectedTimeControl?.updateValueAndValidity();
        } else {
          // Restore required validator for regular flows
          nameControl?.setValidators([Validators.required]);
          nameControl?.updateValueAndValidity();

          // Clear scheduling-specific validators for regular flows
          sessionTitleControl?.clearValidators();
          maxAttendeesControl?.clearValidators();
          this.selectedTimeControl?.clearValidators();

          // Update validity for cleared fields
          sessionTitleControl?.updateValueAndValidity();
          maxAttendeesControl?.updateValueAndValidity();
          this.selectedTimeControl?.updateValueAndValidity();
        }

        if (isSession) {
          // Session event type: sessionTitle and maxAttendees are optional only for create/update flows, not scheduling flow
          if (!this.showScheduleAccordion) {
            sessionTitleControl?.clearValidators();
            maxAttendeesControl?.setValidators([Validators.min(1), Validators.max(100)]); // Min and max validation, not required
          }
          slugControl?.clearValidators();

          // Set default duration to 60 minutes for new session event types
          if (!this.isUpdate) {
            durationSelectionControl?.setValue('60');
            durationInputControl?.setValue('60');
          }
        } else {
          // Regular meeting type: clear session validators, require slug
          if (!this.showScheduleAccordion) {
            sessionTitleControl?.clearValidators();
            maxAttendeesControl?.clearValidators();
          }
          slugControl?.setValidators([Validators.required, Validators.pattern('[a-zA-Z0-9_-]+'), this.uniqueSlug()]);

          // Set default duration to 15 minutes for new regular event types
          if (!this.isUpdate) {
            durationSelectionControl?.setValue('15');
            durationInputControl?.setValue('15');
          }
        }

        sessionTitleControl?.updateValueAndValidity();
        maxAttendeesControl?.updateValueAndValidity();
        slugControl?.updateValueAndValidity();
      }),
    );

    // We don't necessarily need to have the full list of meeting types here,
    // but the dataset should be small and potentially loaded already. We may also want
    // to do client side validation that the bookingLink is unique for form validation
    // when we enable them to change it. As an alternative we could load just the one
    // meeting type for the update mode and call an API to list just bookingLinks for
    // the user, but the performance improvements should be trivial.
    this.meetingTypes$ = this.storeService.loadMeetingTypes({ calendarId: this.calendarId, returnCache: true });
    this.meetingTypeToUpdate$ = this.meetingTypes$.pipe(
      first(),
      tap((meetingTypes) => {
        this.usedSlugs = meetingTypes
          .filter((mt) => mt.meetingTypeSlug !== meetingTypeId && mt.id !== meetingTypeId)
          .map((mt) => mt.meetingTypeSlug);
        // A meeting type starts by having its slug equal its identifiers.
        // We allow a meeting type to save its own slug
      }),
      map((meetingTypes) => {
        const meetingType = meetingTypes.find((mt) => mt.meetingTypeSlug === meetingTypeId || mt.id === meetingTypeId);
        this.originalSlug = meetingType ? meetingType.meetingTypeSlug : '';
        return meetingType;
      }),
    );

    this.showAlert$ = combineLatest([this.teamMembers$, this.selectedTeamMembersSubject]).pipe(
      map(([teamMembers, selected]) => {
        if (!selected.length) return false;

        const selectedIds = selected.map((m) => m.userId);
        return teamMembers.some((m) => selectedIds.includes(m.userId) && (!m.isConfigured || !m.isConnected));
      }),
    );

    // Wait for both meeting types and host availability to load before hiding the loader
    this.subscriptions.push(
      combineLatest([this.meetingTypes$, this.hostAvailability$])
        .pipe(
          first(),
          finalize(() => {
            this.initialLoadComplete = true;
            this.loadingSubject.next(false);
          }),
        )
        .subscribe(),
    );

    this.subscriptions.push(
      combineLatest([
        this.meetingTypeToUpdate$,
        this.storeService.canSendSms$
      ]).pipe(first()).subscribe(([meetingType, canSendSms]) => {
        this.canSendSms = canSendSms; // Update the component property
        
        if (!meetingType) {
          // New form creation - initialize form controls based on canSendSms
          const controls = this.form.controls;
          if (canSendSms) {
            // Enable controls and set default values
            controls.enableEmailField.enable({ emitEvent: false });
            controls.enablePhoneNumberField.enable({ emitEvent: false });
            controls.enableEmailField.setValue(true, { emitEvent: false });
            controls.enablePhoneNumberField.setValue(false, { emitEvent: false });
            controls.notificationType.setValue(NotificationType.EMAIL, { emitEvent: false });
          } else {
            // Disable controls and set default values
            controls.enableEmailField.setValue(true, { emitEvent: false });
            controls.enablePhoneNumberField.setValue(false, { emitEvent: false });
            controls.notificationType.setValue(NotificationType.EMAIL, { emitEvent: false });
            controls.enableEmailField.disable({ emitEvent: false });
            controls.enablePhoneNumberField.disable({ emitEvent: false });
          }
          // Mark form as initialized
          this.formInitialized = true;
          this.setAddressBasedOnContext();
          return;
        }
        
        this.meetingId = meetingType.id;
        this.meetingIdNotNull = true;

        // Initialize calendar now that we have the meetingId (if hostPreferences are also loaded)
        if (this.showScheduleAccordion && this.hostPreferencesLoaded) {
          this.initializeScheduleCalendar();
        }

        // Also check if this is a session event type and initialize calendar
        this.subscriptions.push(
          this.isSessionEventType$.pipe(take(1)).subscribe((isSession) => {
            if (isSession && this.hostPreferencesLoaded) {
              this.initializeScheduleCalendar();
            }
          }),
        );

        const controls = this.form.controls;
        controls.id.setValue(meetingType.id);
        this.eventTypeName = (meetingType.name || '').trim();
        controls.name.setValue(this.eventTypeName);
        controls.description.setValue((meetingType.description || '').trim());
        controls.color.setValue(meetingType.hexColor);
        controls.slug.setValue(meetingType.meetingTypeSlug);
        controls.emailSubject.setValue((meetingType.emailSubject || '').trim());
        controls.emailDescription.setValue((meetingType.emailDescription || '').trim());

        if (meetingType.sessionTitle) {
          controls.sessionTitle.setValue(meetingType.sessionTitle.trim());
        }
        if (meetingType.attendeeCount) {
          controls.maxAttendees.setValue(meetingType.attendeeCount);
        }
        if (meetingType.registrationCutoff) {
          controls.registrationCutoffValue.setValue(meetingType.registrationCutoff.value || 0);
          controls.registrationCutoffUnit.setValue(meetingType.registrationCutoff.unit || 0);
        }

        this.customFields.clear();
        controls.availabilityIncrement.setValue(meetingType.availabilityIncrement);
        controls.bufferDurationAfterMeeting.setValue(meetingType.bufferDurationAfterMeeting);
        controls.bufferDurationBeforeMeeting.setValue(meetingType.bufferDurationBeforeMeeting);
        controls.noticeTime.setValue(meetingType.noticeTime);
        controls.isVideoLinkRequired.setValue(!meetingType.isVideoLinkDisabled);
        
        // Set email and phone based on canSendSms flag (use parameter, not this.canSendSms)
        if (canSendSms) {
          // Proto3 strips false boolean values, so undefined means false
          // Set values BEFORE enabling to prevent valueChanges from firing with wrong values
          const emailRequired = (meetingType as any).isEmailRequired ?? false;
          const phoneRequired = (meetingType as any).isPhoneNumberRequired ?? false;
          
          // Set values with emitEvent: false to prevent toggle subscription from firing during initialization
          controls.enableEmailField.setValue(emailRequired, { emitEvent: false });
          controls.enablePhoneNumberField.setValue(phoneRequired, { emitEvent: false });
          
          // Now enable the controls after values are set
          controls.enableEmailField.enable({ emitEvent: false });
          controls.enablePhoneNumberField.enable({ emitEvent: false });
          
          // Convert API string value to enum number
          let notificationType = NotificationType.EMAIL;
          const apiNotificationTypeValue = (meetingType as any).notificationType;
          
          if (typeof apiNotificationTypeValue === 'string') {
            // API returns string like "SMS" or "EMAIL" (all caps)
            const apiValueUpper = apiNotificationTypeValue.toUpperCase();
            if (apiValueUpper === 'SMS') {
              notificationType = NotificationType.SMS;
            } else if (apiValueUpper === 'Email') {
              notificationType = NotificationType.EMAIL;
            } else if (apiValueUpper === 'Both') {
              // When both are required, notificationType should be 0 or 1, not 2
              // Default to EMAIL (0) if API returns BOTH
              notificationType = NotificationType.EMAIL;
            }
          } else if (typeof apiNotificationTypeValue === 'number') {
            // API returns enum number
            notificationType = apiNotificationTypeValue;
            // If both are required and API returns BOTH (2), default to EMAIL (0)
            if (emailRequired && phoneRequired && notificationType === NotificationType.BOTH) {
              notificationType = NotificationType.EMAIL;
            }
          }
          
          // Set notificationType with emitEvent: false to prevent toggle subscription from firing
          controls.notificationType.setValue(notificationType, { emitEvent: false });
          
        } else {
          // When canSendSms is false: email is required (true), phone is not required (false)
          // Set values with emitEvent: false to prevent toggle subscription from firing
          controls.enableEmailField.setValue(true, { emitEvent: false });
          controls.enablePhoneNumberField.setValue(false, { emitEvent: false });
          controls.notificationType.setValue(NotificationType.EMAIL, { emitEvent: false });
          
          controls.enableEmailField.disable({ emitEvent: false });
          controls.enablePhoneNumberField.disable({ emitEvent: false });
        }
        
        if (meetingType.location === '') {
          this.setAddressBasedOnContext();
        } else {
          controls.inPersonAddress.setValue((meetingType.location || '').trim());
        }
        if (meetingType.locationType !== MeetingLocationType.VIDEO) {
          controls.meetingLocation.setValue('inPerson');
          if (meetingType.locationType === MeetingLocationType.IN_PERSON_USER_SITE) {
            controls.radioSelection.setValue('userSite');
            controls.inPersonAddress.setValue((meetingType.location || '').trim());
          } else {
            controls.radioSelection.setValue('clientSite');
            controls.locationGuidelines.setValue((meetingType.locationGuidelines || '').trim());
          }
        }

        controls.radioChoice.setValue(
          meetingType?.meetingType ? meetingType?.meetingType : TeamEventMeetingType.ROUND_ROBIN,
        );

        meetingType.form.fields.forEach((field: MeetingTypeFormField) => {
          this.customFields.push(this.groupFromFormField(field));
        });
        const durationMinutes = durationStringToMinutes({ duration: meetingType.duration }) + '';
        if (this.durations.includes(durationMinutes)) {
          controls.durationSelection.setValue(durationMinutes);
        } else {
          controls.durationSelection.setValue(this.customDuration);
        }
        controls.durationInput.setValue(durationMinutes);
        if (meetingType.hostUserIds.length > 0) {
          // First, get the selected team members
          this.selectedTeamMembers = this.allTeamMembers.filter((member) =>
            meetingType.hostUserIds.includes(member.userId),
          );
          this.selectedTeamMembersSubject.next(this.selectedTeamMembers);

          // Apply priority order if it exists and we're in priority assignment mode
          const meetingTypeAny = meetingType as any;
          if (
            meetingTypeAny.hostOrderDetails &&
            meetingType.meetingType === TeamEventMeetingType.PRIORITY_ROUND_ROBIN
          ) {
            if (this.selectedTeamMembers.length === 0) {
              // Store the hostOrderDetails to apply later when team members are loaded
              this.pendingHostOrderDetails = meetingTypeAny.hostOrderDetails;
              this.pendingMeetingType = meetingType.meetingType;
              this.hostUserIds = meetingType.hostUserIds; // Keep original order for now
            } else {
              this.applyHostOrderToSelectedMembers(meetingTypeAny.hostOrderDetails);
              // hostUserIds is already updated in applyHostOrderToSelectedMembers
              this.cdr.detectChanges();
            }
          } else {
            // For non-priority modes, use the original order
            this.hostUserIds = meetingType.hostUserIds;
          }
        } else {
          this.subscriptions.push(
            this.teamMembers$.subscribe((hostUsers) => {
              this.hostUserIds = hostUsers.map((user) => user.userId);
              this.selectedTeamMembers = this.allTeamMembers.filter((member) =>
                this.hostUserIds.includes(member.userId),
              );
              this.selectedTeamMembersSubject.next(this.selectedTeamMembers);
            }),
          );
        }

        this.dateRangeConfig = meetingType.dateRange;

        // Initialize form with date range data if available
        if (this.dateRangeConfig) {
          if (!this.dateRangeConfig.dateRangeType) {
            this.dateRangeConfig.dateRangeType = DateRangeType.RELATIVE;
          }

          this.initializeDateRangeForm();
        }
        
        // Mark form as initialized to allow toggle subscription to work
        this.formInitialized = true;
        
        // Trigger change detection to update the UI with loaded values
        this.cdr.detectChanges();
      }),
    );

    // Watch for toggle changes and automatically update notificationType
    this.subscriptions.push(
      combineLatest([
        this.form.controls.enableEmailField.valueChanges.pipe(startWith(this.form.controls.enableEmailField.value)),
        this.form.controls.enablePhoneNumberField.valueChanges.pipe(startWith(this.form.controls.enablePhoneNumberField.value))
      ]).pipe(
        skip(1) // Skip initial emission from startWith
      ).subscribe(([emailEnabled, phoneEnabled]) => {
        // Don't process toggle changes until form is initialized from API
        if (!this.formInitialized) {
          return;
        }
        
        // Only update if canSendSms is true (otherwise fields are disabled anyway)
        if (this.canSendSms) {
          const previousNotificationType = this.form.controls.notificationType.value;
          let newNotificationType: NotificationType | null = null;
          
          if (emailEnabled && phoneEnabled) {
            // Both required - keep current selection or default to EMAIL
            const currentValue = this.form.controls.notificationType.value;
            if (currentValue !== NotificationType.EMAIL && currentValue !== NotificationType.SMS) {
              newNotificationType = NotificationType.EMAIL;
            }
          } else if (phoneEnabled && !emailEnabled) {
            // Only phone required - set to SMS
            newNotificationType = NotificationType.SMS;
          } else if (emailEnabled && !phoneEnabled) {
            // Only email required - set to EMAIL
            newNotificationType = NotificationType.EMAIL;
          }
          
          if (newNotificationType !== null && newNotificationType !== previousNotificationType) {
            this.form.controls.notificationType.setValue(newNotificationType);
          }
        }
      })
    );

    this.filteredTeamMembers = this.teamMemberCtrl.valueChanges.pipe(
      startWith(''),
      switchMap((searchTerm: any) => this.teamMembers$.pipe(map(() => this._filter(searchTerm?.displayName || '')))),
    );
  }

  hasSelectedAvailability(isAvailabilitySelected: boolean) {
    this.isAvailabilitySelected = isAvailabilitySelected;
  }

  updatedSelectedAvailabilityValue(availability: DailyAvailability) {
    this.availabilitySelected = availability;
    this.validateAvailabilitySlots();
  }

  private validateAvailabilitySlots(): void {
    if (this.isAvailabilitySelected && this.radioChoiceValue === TeamEventMeetingType.MULTI_HOST) {
      let isValidSlot = false;
      [isValidSlot, this.displayAvailabilityInsufficientDurationError] = this.hasAvailableSlotsForPickedTeamMembers();
      this.displayAvailabilitySelectionSlotError = !isValidSlot;
    } else {
      this.displayAvailabilitySelectionSlotError = false;
      this.displayAvailabilityInsufficientDurationError = false;
    }
  }

  setAddressBasedOnContext(): void {
    if (this.context === 'SMB') {
      this.getSMBAddress(this.businessId).then((ad) => {
        this.address = ad;
        this.form.controls['inPersonAddress'].setValue(this.address);
      });
    } else {
      const formattedAddress$ = this.getPartnerAddress$();
      this.subscriptions.push(
        formattedAddress$.subscribe((formattedAddress) => {
          this.address = formattedAddress;
          this.form.controls['inPersonAddress'].setValue(this.address);
        }),
      );
    }
  }

  loadCalendar(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.storeService.loadCalendar(this.calendarId).subscribe(
        (calendar) => {
          this.calendar = calendar; // Store the response in a variable
          this.applicationContext = calendar.applicationContext;

          this.context = this.applicationContext.user_context;

          if (this.context === 'SMB') {
            this.businessId = this.applicationContext.business_id;
          } else {
            this.partnerid = this.applicationContext.partner_id;
          }

          resolve();
        },
        (error) => {
          reject(error);
        },
      );
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.loadingSubject.complete();
  }

  get isCustomDuration(): boolean {
    return this.durationSelectionControl?.value === this.customDuration;
  }

  get slugChanged(): boolean {
    return this.isUpdate && this.originalSlug !== (this.form.controls.slug.value as string);
  }

  // Date range getters
  get isRelativeMode(): boolean {
    return this.dateRangeTypeControl?.value === DateRangeType.RELATIVE;
  }

  get isCustomMode(): boolean {
    return this.dateRangeTypeControl?.value === DateRangeType.CUSTOM;
  }

  get radioChoiceValue(): TeamEventMeetingType | null {
    return this.form?.controls?.radioChoice?.value || null;
  }

  // Form control getters
  get nameControl() {
    return this.form.get('name');
  }

  get sessionTitleControl() {
    return this.form.get('sessionTitle');
  }

  get durationSelectionControl() {
    return this.form.get('durationSelection');
  }

  get durationInputControl() {
    return this.form.get('durationInput');
  }

  get descriptionControl() {
    return this.form.get('description');
  }

  get colorControl() {
    return this.form.get('color');
  }

  get meetingLocationControl() {
    return this.form.get('meetingLocation');
  }

  get radioSelectionControl() {
    return this.form.get('radioSelection');
  }

  get emailSubjectControl() {
    return this.form.get('emailSubject');
  }

  get emailDescriptionControl() {
    return this.form.get('emailDescription');
  }

  get enableEmailFieldControl() {
    return this.form.get('enableEmailField');
  }

  get enablePhoneNumberFieldControl() {
    return this.form.get('enablePhoneNumberField');
  }

  get notificationTypeControl() {
    return this.form.get('notificationType');
  }

  // Logic to determine if both fields are required
  get areBothFieldsRequired(): boolean {
    return this.enableEmailFieldControl?.value === true && this.enablePhoneNumberFieldControl?.value === true;
  }

  // Logic to check if neither email nor phone is required
  get areNoFieldsRequired(): boolean {
    return this.enableEmailFieldControl?.value === false && this.enablePhoneNumberFieldControl?.value === false;
  }

  // Logic to determine which field should show the badge
  get showEmailBadge(): boolean {
    // Don't show badge if both fields are required
    if (this.areBothFieldsRequired) return false;
    
    const emailRequired = this.enableEmailFieldControl?.value === true;
    if (!emailRequired) return false;
    
    const phoneRequired = this.enablePhoneNumberFieldControl?.value === true;
    if (!phoneRequired) return true; // Only email required
    
    return false;
  }

  get showPhoneBadge(): boolean {
    // Don't show badge if both fields are required
    if (this.areBothFieldsRequired) return false;
    
    const phoneRequired = this.enablePhoneNumberFieldControl?.value === true;
    if (!phoneRequired) return false;
    
    const emailRequired = this.enableEmailFieldControl?.value === true;
    if (!emailRequired) return true; // Only phone required
    
    return false;
  }

  get inPersonAddressControl() {
    return this.form.get('inPersonAddress');
  }

  get locationGuidelinesControl() {
    return this.form.get('locationGuidelines');
  }

  get slugControl() {
    return this.form.get('slug');
  }

  get customFieldsControl() {
    return this.form.get('customFields');
  }

  get availabilityIncrementControl() {
    return this.form.get('availabilityIncrement');
  }

  get bufferDurationAfterMeetingControl() {
    return this.form.get('bufferDurationAfterMeeting');
  }

  get bufferDurationBeforeMeetingControl() {
    return this.form.get('bufferDurationBeforeMeeting');
  }

  get noticeTimeControl() {
    return this.form.get('noticeTime');
  }

  get isVideoLinkRequiredControl() {
    return this.form.get('isVideoLinkRequired');
  }

  get maxAttendeesControl() {
    return this.form.get('maxAttendees');
  }

  get registrationCutoffValueControl() {
    return this.form.get('registrationCutoffValue');
  }

  get registrationCutoffUnitControl() {
    return this.form.get('registrationCutoffUnit');
  }

  get dateRangeTypeControl() {
    return this.form.get('dateRangeType');
  }

  get relativeValueControl() {
    return this.form.get('relativeValue');
  }

  get relativeUnitControl() {
    return this.form.get('relativeUnit');
  }

  get startDateControl() {
    return this.form.get('startDate');
  }

  get endDateControl() {
    return this.form.get('endDate');
  }

  get selectedTimeControl() {
    return this.form.get('selectedTime');
  }

  get idControl() {
    return this.form.get('id');
  }

  get radioChoiceControl() {
    return this.form.get('radioChoice');
  }

  get relativeValueError(): string | null {
    const control = this.relativeValueControl;
    if (control?.hasError('required')) {
      return 'Value is required';
    }
    if (control?.hasError('min')) {
      return 'Value must be at least 1';
    }
    return null;
  }
  get hasDateRangeError(): boolean {
    return this.form.hasError('dateRangeInvalid');
  }

  uniqueSlug(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      const slug = control.value as string;
      return this.usedSlugs.includes(slug)
        ? {
            unique: {
              usedSlugs: this.usedSlugs,
              slug: slug,
            },
          }
        : null;
    };
  }

  select(event: boolean, hostUser: HostUser): void {
    if (this.hostUserIds.indexOf(hostUser.userId) < 0) {
      this.hostUserIds = [...this.hostUserIds, hostUser.userId];
      // Clear date and time selection when user is added
      this.clearDateAndTimeSelection();
    } else {
      this.hostUserIds = this.hostUserIds.filter((userId) => userId !== hostUser.userId);
      // Clear date and time selection when user is removed
      this.clearDateAndTimeSelection();
    }
  }

  onShow(checked: boolean): void {
    this.isShowContent = checked;
  }

  revertSlug(): void {
    this.form.controls.slug.setValue(this.originalSlug);
  }

  fetchMeetingDuration() {
    let duration = this.form.controls?.durationSelection?.value || 0;
    if (duration === this.customDuration) {
      duration = this.form.controls.durationInput.value;
    }

    duration = parseInt(duration, 10);

    // Don't include buffer durations in scheduling flow
    if (!this.showScheduleAccordion) {
      duration += this.form.controls.bufferDurationAfterMeeting.value / 60;
      duration += this.form.controls.bufferDurationBeforeMeeting.value / 60;
    }

    return duration;
  }

  hasAvailableSlotsForPickedTeamMembers() {
    let isOverLapError = false;
    if (
      !this.selectedTeamMembers?.length ||
      !this.hostAvailability$ ||
      !this.availabilitySelected ||
      this.radioChoiceValue !== TeamEventMeetingType.MULTI_HOST
    ) {
      return [false, isOverLapError];
    }
    // Get the required duration in minutes

    const duration = this.fetchMeetingDuration();
    this.meetingDuration = duration;
    // We need to synchronously get the latest value of hostAvailability$
    let hostAvailability: Map<string, AvailabilityRuleList> = new Map();
    this.hostAvailability$.pipe(take(1)).subscribe((availability) => {
      hostAvailability = availability;
    });
    // Only check days that are present in this.availabilitySelected.days (selected days)
    for (const [day, selectedRanges] of this.availabilitySelected.days.entries()) {
      if (!selectedRanges?.length) continue;
      // Get the common slots for this day
      const commonSlots = getCommonTimeSlots(
        day,
        hostAvailability,
        this.selectedTeamMembers.map((m) => m.userId),
      );
      for (const commonSlot of commonSlots) {
        const commonStart = commonSlot.from.hours * 60 + commonSlot.from.minutes;
        const commonEnd = commonSlot.to.hours * 60 + commonSlot.to.minutes;
        for (const selectedRange of selectedRanges) {
          const selStart = selectedRange.start.getHours() * 60 + selectedRange.start.getMinutes();
          const selEnd = selectedRange.end.getHours() * 60 + selectedRange.end.getMinutes();
          // Find the overlap
          const overlapStart = Math.max(commonStart, selStart);
          const overlapEnd = Math.min(commonEnd, selEnd);
          if (overlapEnd - overlapStart >= duration) {
            return [true, false];
          } else {
            if (overlapEnd - overlapStart >= 0) {
              isOverLapError = true;
            }
          }
        }
      }
    }
    return [false, isOverLapError];
  }

  getSlugValidationError(): string {
    const errors = this.form.controls.slug.errors;
    if (errors.required) {
      return this.translate.instant('MEETING_SCHEDULER.BOOKING_LINK.LINK_VALIDATION_REQUIRED');
    } else if (errors.pattern) {
      return this.translate.instant('MEETING_SCHEDULER.BOOKING_LINK.LINK_VALIDATION_PATTERN');
    } else if (errors.unique) {
      return this.translate.instant('MEETING_SCHEDULER.BOOKING_LINK.LINK_VALIDATION_UNIQUE');
    }
    return this.translate.instant('MEETING_SCHEDULER.BOOKING_LINK.LINK_VALIDATION_UNKNOWN');
  }

  setColor(color: string): void {
    this.form.controls.color.setValue(color);
  }

  validateAvailability(): boolean {
    const [hasAvailableSlots, isOverlapErr] = this.hasAvailableSlotsForPickedTeamMembers();
    if (!hasAvailableSlots) {
      if (isOverlapErr) {
        this.displayAvailabilityInsufficientDurationError = true;
      } else {
        this.displayAvailabilitySelectionSlotError = true;
      }
      return false;
    }
    return true;
  }

  /**
   * Validator to ensure selected team members are calendar connected or configured
   */
  private teamMembersConfiguredValidator(): ValidatorFn {
    return () => {
      if (!this.selectedTeamMembers?.length) {
        return null;
      }

      const hasInvalidMembers = this.selectedTeamMembers.some((member) => !member.isConfigured || !member.isConnected);

      return hasInvalidMembers ? { invalidTeamMembers: true } : null;
    };
  }

  /**
   * Scrolls to the team member section
   */
  private scrollToTeamMemberSection(): void {
    setTimeout(() => {
      const element =
        this.teamMemberSection?.nativeElement || document.querySelector('[data-testid="team-member-section"]');

      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }, 100);
  }

  async createOrUpdateMeetingType(): Promise<void> {
    this.teamMemberCtrl.markAsTouched();

    // Check if Form or team members are invalid
    if (this.form.invalid || this.teamMemberCtrl.invalid) {
      this.form.markAllAsTouched();

      // Scroll to team member section if that's where the error is
      if (this.teamMemberCtrl.invalid) {
        this.scrollToTeamMemberSection();
      }

      return;
    }

    // Handle schedule flow differently
    if (this.showScheduleAccordion) {
      await this.handleScheduleSession();
      return;
    }

    // Handle team member selection
    // Team members are required only for team event types
    let isTeamRequired = false;
    this.subscriptions.push(
      this.showHostUserSection$.subscribe((value: boolean) => {
        isTeamRequired = value;
      }),
    );

    if (this.isSessionEvent) {
      isTeamRequired = true;
      this.form.controls.radioChoice.setValue(TeamEventMeetingType.MULTI_HOST);
    }

    const controls = this.form.controls;

    if (isTeamRequired && this.selectedTeamMembers.length < 1) {
      this.teamMemberInput.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const minRequiredHosts = this.isSessionEvent ? 1 : 2;

    if (
      isTeamRequired &&
      (this.selectedTeamMembers.length < minRequiredHosts || this.selectedTeamMembers.length > 5) &&
      controls.radioChoice.value === TeamEventMeetingType.MULTI_HOST
    ) {
      this.teamMemberInput.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (
      controls.radioChoice.value === TeamEventMeetingType.MULTI_HOST &&
      !this.isAvailabilitySelected &&
      !this.isSessionEvent
    ) {
      this.displayAvailabilitySelectionError = true;
      this.scrollToAvailabilityPanel();
      return;
    }

    // Validate for atleast one valid slot available
    // Validatas based on team member's selected, time slots provided, days enabled & host general availability
    // Only required for multihost meeting in other scenarios value is overridden instead of general availability
    if (
      controls.radioChoice.value === TeamEventMeetingType.MULTI_HOST &&
      !this.validateAvailability() &&
      !this.isSessionEvent
    ) {
      this.scrollToAvailabilityPanel();
      return;
    }

    let locationType = MeetingLocationType.VIDEO as MeetingLocationType;
    let location = '';
    let locationGuidelines = '';

    if (controls.meetingLocation.value === 'inPerson') {
      if (controls.radioSelection.value === 'userSite') {
        locationType = MeetingLocationType.IN_PERSON_USER_SITE;
        location = controls.inPersonAddress.value;
      } else if (controls.radioSelection.value === 'clientSite') {
        locationType = MeetingLocationType.IN_PERSON_CLIENT_SITE;
        locationGuidelines = controls.locationGuidelines.value;
      }
    }

    // Validate that at least one field is required when canSendSms is true
    if (this.canSendSms) {
      // Use the same getters as areNoFieldsRequired for consistency
      const emailRequired = this.enableEmailFieldControl?.value === true;
      const phoneRequired = this.enablePhoneNumberFieldControl?.value === true;
      
      // If neither field is required, show error and prevent update
      if (!emailRequired && !phoneRequired) {
        this.alerts.openErrorSnack('MEETING_SCHEDULER.BOOKING_LINK.QUESTIONS_SECTION.WARNING_MESSAGE');
        return;
      }
    }

    // Get values including disabled controls
    const emailRequired = this.canSendSms ? controls.enableEmailField.value : true;
    const phoneRequired = this.canSendSms ? controls.enablePhoneNumberField.value : false;

    // Calculate notificationType and set required fields based on user's requirements:
    // - If email is selected: isEmailRequired=true, notificationType=0
    // - If phone is selected: isPhoneNumberRequired=true, notificationType=1
    // - If both are required: isEmailRequired=true, isPhoneNumberRequired=true, notificationType=0 or 1 (based on button selection)
    let notificationType: NotificationType;
    let isEmailRequired: boolean;
    let isPhoneNumberRequired: boolean;
    
    if (emailRequired && phoneRequired) {
      // Both required - use user's selection from the control (0=EMAIL or 1=SMS)
      notificationType = controls.notificationType.value as NotificationType;
      isEmailRequired = true;
      isPhoneNumberRequired = true;
    } else if (phoneRequired && !emailRequired) {
      // Only phone required - use SMS (1)
      notificationType = NotificationType.SMS;
      isEmailRequired = false;
      isPhoneNumberRequired = true;
    } else {
      // Only email required (or canSendSms is false) - use EMAIL (0)
      notificationType = NotificationType.EMAIL;
      isEmailRequired = true;
      isPhoneNumberRequired = false;
    }
    
    const mt = {
      calendarId: this.calendarId,
      id: controls.id.value as string,
      name: controls.name.value as string,
      sessionTitle: controls.sessionTitle.value as string,
      duration: (controls.durationInput.value as string) + 'm',
      description: controls.description.value as string,
      hexColor: controls.color.value as string,
      meetingTypeSlug: controls.slug.value as string,
      emailSubject: controls.emailSubject.value as string,
      emailDescription: controls.emailDescription.value as string,
      availabilityIncrement: controls.availabilityIncrement.value as number,
      bufferDurationAfterMeeting: controls.bufferDurationAfterMeeting.value as number,
      bufferDurationBeforeMeeting: controls.bufferDurationBeforeMeeting.value as number,
      noticeTime: controls.noticeTime.value as number,
      isVideoLinkDisabled: !controls.isVideoLinkRequired.value as boolean,
      locationType: locationType as MeetingLocationType,
      location: location as string,
      locationGuidelines: locationGuidelines as string,
      attendeeCount: controls.maxAttendees.value as number,
      registrationCutoff: {
        value: controls.registrationCutoffValue.value as number,
        unit: controls.registrationCutoffUnit.value as number,
      },
      form: {
        fields: controls.customFields.value.map((field: MeetingTypeFormField) => {
          field.id = this.deriveIDFromLabel(field.label);
          return field;
        }),
      },
      hostUserIds: this.hostUserIds,
      meetingType: await this.getMeetingType(controls.radioChoice.value),
      dateRange: this.buildDateRangeConfig(),
      hostOrderDetails: this.buildHostOrderConfig(controls.radioChoice.value),
      bookingUrl: '',
      hostUsers: [],
      isEmailRequired: isEmailRequired,
      isPhoneNumberRequired: isPhoneNumberRequired,
      notificationType: notificationType as any, // Send as enum number (0=EMAIL, 1=SMS, 2=BOTH)
    } as MeetingType;
    
    this.meetingId = mt.id;

    if (this.isUpdate) {
      // Track session event type updation with additional details
      if (this.isSessionEvent) {
        this.analyticsService.trackEvent(
          POSTHOG_KEYS.SESSION_EVENT_TYPE_UPDATED,
          POSTHOG_CATEGORIES.USER,
          POSTHOG_ACTIONS.CLICK,
        );
      }
      const newSlug = controls.slug.value as string;
      if (this.originalSlug !== newSlug) {
        this.dialog.open(UpdateBookingLinkConfirmationDialogComponent, {
          maxWidth: '500px',
          data: {
            originalSlug: this.originalSlug,
            newSlug: newSlug,
            confirmationCallback: () => from(this.updateMeetingType(mt.id, mt)),
          },
        });
      } else {
        await this.updateMeetingType(mt.id, mt);
      }
    } else {
      try {
        const id = await this.storeService.createMeetingType(mt, this.hostid);
        this.meetingId = id; // Update meetingId with the actual created ID

        this.analyticsService.trackEvent(
          POSTHOG_KEYS.EVENT_TYPE_CREATED,
          POSTHOG_CATEGORIES.USER,
          POSTHOG_ACTIONS.CLICK,
        );

        // Track multi-host meeting type creation with additional details
        if (controls.radioChoice.value === TeamEventMeetingType.MULTI_HOST) {
          this.analyticsService.trackEvent(
            POSTHOG_KEYS.MULTI_HOST_EVENT_TYPE_CREATED,
            POSTHOG_CATEGORIES.USER,
            POSTHOG_ACTIONS.CLICK,
          );
        }
        // Track priority assignment meeting type creation with additional details
        if (controls.radioChoice.value === TeamEventMeetingType.PRIORITY_ROUND_ROBIN) {
          this.analyticsService.trackEvent(
            POSTHOG_KEYS.PRIORITY_ASSIGNMENT_EVENT_TYPE_CREATED,
            POSTHOG_CATEGORIES.USER,
            POSTHOG_ACTIONS.CLICK,
          );
        }
        // Track session event type creation with additional details
        if (this.isSessionEvent) {
          this.analyticsService.trackEvent(
            POSTHOG_KEYS.SESSION_EVENT_TYPE_CREATED,
            POSTHOG_CATEGORIES.USER,
            POSTHOG_ACTIONS.CLICK,
          );
        }

        this.alerts.openSuccessSnack('MEETING_SCHEDULER.BOOKING_LINK.CREATED_SUCCESS');

        // Check if we should navigate to schedule page instead of meeting list
        if (this.shouldNavigateToSchedule) {
          this.shouldNavigateToSchedule = false; // Reset flag
          await this.navigateToSchedulePage();
        } else {
          this.navigateToMeetingList();
        }

        this.setAvailability(id);
      } catch (err) {
        // Reset flag on error
        this.shouldNavigateToSchedule = false;
        this.alerts.openErrorSnack('MEETING_SCHEDULER.BOOKING_LINK.CREATED_ERROR');
        console.error(err);
      }
    }
  }

  async handleScheduleSession(): Promise<void> {
    try {
      const controls = this.form.controls;

      // Mark all fields as touched to show validation errors
      this.form.markAllAsTouched();

      // Check if form is valid
      if (this.form.invalid) {
        this.alerts.openErrorSnack('Please fill in all required fields correctly.');
        return;
      }

      // Create session start time using moment.js (same logic as schedule-meeting-dialog)
      let sessionStart: Date | null = null;
      if (this.selectedDate && controls.selectedTime.value) {
        const dateString = moment(this.selectedDate).format('YYYY-MM-DD');
        const dateTimeString = `${dateString} ${controls.selectedTime.value}`;
        const localDateTime = moment.tz(dateTimeString, 'YYYY-MM-DD hh:mm A', this.timezone);
        sessionStart = localDateTime.toDate();
      }

      // Determine meeting type for validation
      const isVideoMeeting = controls.meetingLocation.value === 'video';

      // Only validate date and time in snackbar for schedule flow - other validations are handled by form validators
      if (this.showScheduleAccordion && !sessionStart) {
        this.alerts.openErrorSnack(
          this.translate.instant('MEETING_SCHEDULER.BOOKING_LINK.DATE_TIME_VALIDATION_REQUIRED'),
        );
        return;
      }

      // Prepare form data for the API call based on HostBookSessionRequestInterface
      const locationType = isVideoMeeting ? 0 : 1; // VIDEO = 0, IN_PERSON = 1
      const formData: any = {
        title: controls.sessionTitle.value,
        locationType: locationType,
        duration: (controls.durationInput.value || '60') + 'm',
        description: controls.description.value || '',
        maxAttendees: controls.maxAttendees.value || null,
        logoUrl: null, // Add if available in form
        hostUsers: this.selectedTeamMembers.length > 0 ? this.selectedTeamMembers : null,
        registrationCutoff: {
          value: controls.registrationCutoffValue.value || 0,
          unit: controls.registrationCutoffUnit.value || 0,
        },
        timeSlot: sessionStart
          ? {
              start: sessionStart,
              end: sessionStart, // Will be calculated by backend based on duration
            }
          : null,
        sessionSlug: controls.slug.value || null,
        start: sessionStart,
        hostId: this.hostid,
        calendarId: this.calendarId,
      };

      // Only include location if it's not a video meeting
      if (!isVideoMeeting) {
        formData.location = controls.inPersonAddress.value || '';
      }

      // Call the schedule session API
      const response = await this.storeService.scheduleSession(this.meetingId, formData);

      // Show success dialog with booking URL
      this.dialog.open(BookingUrlDialogComponent, {
        maxWidth: '600px',
        disableClose: false,
        data: {
          bookingUrl: response.bookingUrl,
          meetingId: response.meetingId,
        },
      });

      // Track session event type scheduled
      this.analyticsService.trackEvent(
        POSTHOG_KEYS.SESSION_EVENT_TYPE_SCHEDULED,
        POSTHOG_CATEGORIES.USER,
        POSTHOG_ACTIONS.CLICK,
      );
    } catch (err) {
      this.alerts.openErrorSnack('Failed to schedule session. Please try again.');
      console.error('Error scheduling session:', err);
    }
  }

  async updateMeetingType(id: string, meetingType: MeetingType): Promise<void> {
    try {
      await this.storeService.updateMeetingType(id, this.hostid, meetingType);
      this.analyticsService.trackEvent(POSTHOG_KEYS.EVENT_TYPE_UPDATED, POSTHOG_CATEGORIES.USER, POSTHOG_ACTIONS.CLICK);
      this.setAvailability(id);
      this.alerts.openSuccessSnack('MEETING_SCHEDULER.BOOKING_LINK.UPDATED_SUCCESS');
      this.navigateToMeetingList();
    } catch (err) {
      this.alerts.openErrorSnack('MEETING_SCHEDULER.BOOKING_LINK.UPDATED_ERROR');
      console.error(err);
    }
  }

  async createAndSchedule(): Promise<void> {
    try {
      // Set flag to indicate we want to navigate to schedule page after creation
      this.shouldNavigateToSchedule = true;

      // Create the event type - the navigation will be handled in createOrUpdateMeetingType
      await this.createOrUpdateMeetingType();
    } catch (error) {
      // Reset flag on error
      this.shouldNavigateToSchedule = false;
      console.error('Error in createAndSchedule:', error);
    }
  }

  private async navigateToSchedulePage(): Promise<void> {
    try {
      // Get the calendar type for the current calendar
      const calendarType = await this.getCalendarType(this.calendarId);
      const slug = this.slugControl?.value;

      // Navigate to the schedule page with appropriate query parameters
      await this.router.navigate([this.eventTypePageLink, this.calendarId, slug], {
        queryParams: {
          calendar_type: calendarType,
          event_type: 'Session',
          show_schedule_accordion: 'true',
        },
      });
    } catch (error) {
      console.error('Error navigating to schedule page:', error);
      // Fallback to meeting list if navigation fails
      this.navigateToMeetingList();
    }
  }

  private async getCalendarType(calendarId: string): Promise<string> {
    try {
      // Try to get calendar type from session calendar first
      const sessionCalendar = await this.sessionCalendar$.pipe(take(1)).toPromise();
      if (sessionCalendar && sessionCalendar.id === calendarId) {
        return 'session';
      }

      // Fallback to group calendars
      const groupCalendars = await this.groupCalendars$.pipe(take(1)).toPromise();
      const calendar = groupCalendars?.find((cal) => cal.id === calendarId);
      return calendar ? 'group' : 'session';
    } catch (error) {
      console.error('Error getting calendar type:', error);
      return 'session'; // Default fallback
    }
  }

  async getSMBAddress(businessid: string): Promise<string> {
    const accountGroupProjectionFilter = new ProjectionFilter({
      napData: true,
      accountGroupExternalIdentifiers: true,
      marketingInfo: true,
    });

    const business$ = this.accountGroupService.get(businessid, accountGroupProjectionFilter);

    return combineLatest([this.service.partnerId$, business$])
      .pipe(take(1))
      .toPromise()
      .then(([, business]) => {
        const napData = business?.napData;
        return napData
          ? this.buildAddress(napData.address, napData.city, napData.zip, napData.state, napData.country)
          : '';
      });
  }

  getPartnerAddress$() {
    return this.partnerId$.pipe(
      switchMap((partnerId) => this.whitelabelService.getConfiguration(partnerId)),
      map((config) => {
        const address = config?.mailingConfiguration?.mailingAddress;
        const city = config?.mailingConfiguration?.mailingCity;
        const state = config?.mailingConfiguration?.mailingState;
        const zip = config?.mailingConfiguration?.mailingPostalCode;
        const country = config?.mailingConfiguration?.mailingCountry;
        return this.buildAddress(address, city, zip, state, country);
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  buildAddress(address: string, city: string, zip: string, state: string, country: string): string {
    const parts = [];
    if (address) parts.push(address);
    if (city) parts.push(city);
    if (zip) parts.push(zip);
    if (state) parts.push(state);
    if (country) parts.push(country);
    const formattedAddress = parts.join(', ');
    return formattedAddress;
  }

  get aboutCardDescription(): string {
    const controls = this.form?.controls;

    if (!controls) return '';

    // For schedule flow, don't show name and duration in subtitle
    if (this.showScheduleAccordion) {
      return '';
    }

    const durationShorthand =
      controls.durationInput.value + ' ' + this.translate.instant('MEETING_SCHEDULER.COMMON.DURATIONS.MINUTES');
    if (controls.name.value !== '') {
      let name = controls.name.value;
      const maxLen = 25;
      if (name.length > maxLen) {
        name = name.substr(0, maxLen) + '...';
      }
      return name + ', ' + durationShorthand;
    }
    return durationShorthand;
  }

  get questionsCardDescription(): string {
    const customFields = this.form?.controls?.customFields.value;
    if (customFields) {
      return (
        customFields.length +
        ' ' +
        this.translate.instant('MEETING_SCHEDULER.BOOKING_LINK.QUESTIONS_SECTION.DESCRIPTION_SUFFIX')
      );
    }
    return '';
  }

  describeFieldType(type: FormFieldType): string {
    return this.fieldTypes.get(type) || '';
  }

  addTeamMemberFromInput(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (value) {
      const foundMember = this.allTeamMembers.find(
        (member) => member.displayName.toLowerCase() === value.toLowerCase(),
      );
      if (foundMember && !this.selectedTeamMembers.some((member) => member.userId === foundMember.userId)) {
        this.selectedTeamMembers.push(foundMember);

        this.selectedTeamMembersSubject.next(this.selectedTeamMembers);

        this.hostUserIds = [...this.hostUserIds, foundMember.userId];
        // Clear date and time selection when team member is added
        this.clearDateAndTimeSelection();

        // Validate availability when team member is added
        this.validateAvailabilitySlots();
      }
      event.chipInput?.clear();
      this.teamMemberCtrl.setValue(null);
    }
  }

  removeTeamMember(index: number): void {
    const teamMember = this.selectedTeamMembers[index];
    if (teamMember) {
      this.selectedTeamMembers.splice(index, 1);
      this.selectedTeamMembersSubject.next(this.selectedTeamMembers);
      this.hostUserIds = this.hostUserIds.filter((id) => id !== teamMember.userId);
      // Clear date and time selection when team member is removed
      this.clearDateAndTimeSelection();
      // Validate availability when team member is removed
      this.validateAvailabilitySlots();
      // Update validation
      this.teamMemberCtrl.updateValueAndValidity();
    }
  }

  selectedTeamMember(event: MatAutocompleteSelectedEvent): void {
    const teamMember = event.option.value;
    if (!this.selectedTeamMembers.some((member) => member.userId === teamMember.userId)) {
      this.selectedTeamMembers.push(teamMember);

      this.selectedTeamMembersSubject.next(this.selectedTeamMembers);
      this.hostUserIds = [...this.hostUserIds, teamMember.userId];
      // Clear date and time selection when team member is selected
      this.clearDateAndTimeSelection();
      // Validate availability when team member is selected
      this.validateAvailabilitySlots();
      // Update validation
      this.teamMemberCtrl.updateValueAndValidity();
    }
    if (this.teamMemberInput) this.teamMemberInput.nativeElement.value = '';
    this.teamMemberCtrl.setValue(null);
  }

  private _filter(value: string): any[] {
    const filterValue = typeof value === 'string' ? value.toLowerCase() : '';
    return this.allTeamMembers.filter((teamMember) => teamMember.displayName.toLowerCase().includes(filterValue));
  }

  fieldLabelErrorMessage(label: string): void {
    if (label.length > 0) {
      return this.translate.instant('MEETING_SCHEDULER.BOOKING_LINK.QUESTIONS_SECTION.ERROR_LABEL_MAX', {
        max: this.fieldLabelMax,
      });
    }
    return this.translate.instant('MEETING_SCHEDULER.BOOKING_LINK.QUESTIONS_SECTION.ERROR_LABEL_REQUIRED');
  }

  addFormField(): void {
    if (this.form.controls.customFields.invalid) {
      return;
    }
    const customFields = this.form.controls.customFields as UntypedFormArray;
    customFields.push(
      this.groupFromFormField({
        id: '',
        label: '',
        type: FormFieldType.FORM_FIELD_TYPE_TEXT,
        required: false,
      }),
    );
  }

  changeFieldOrder(event: CdkDragDrop<string[]>): void {
    const customFields = this.form.controls.customFields as UntypedFormArray;
    const controls = customFields.controls;
    const values = customFields.value;
    moveItemInArray(controls, event.previousIndex, event.currentIndex);
    moveItemInArray(values, event.previousIndex, event.currentIndex);
  }

  openAllFields(): void {
    this.accordion.openAll();
  }

  closeAllFields(): void {
    this.accordion.closeAll();
  }

  deleteField(index: number): void {
    const customFields = this.form.controls.customFields as UntypedFormArray;
    customFields.removeAt(index);
  }

  fieldsHaveError(err: string): boolean {
    return this.form.controls.customFields.hasError(err);
  }

  private deriveIDFromLabel(label: string): string {
    return camelCase(label);
  }

  private groupFromFormField(field: MeetingTypeFormField): UntypedFormGroup {
    return this.formBuilder.group({
      id: this.formBuilder.control(this.deriveIDFromLabel(field.label)),
      label: this.formBuilder.control(field.label || '', Validators.maxLength(this.fieldLabelMax)),
      type: this.formBuilder.control(field.type || FormFieldType.FORM_FIELD_TYPE_TEXT),
      required: this.formBuilder.control(field.required || false),
    });
  }

  private noDuplicates(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      const fields = control.value as MeetingTypeFormField[];
      const ids = fields.map((field) => this.deriveIDFromLabel(field.label)).filter((id) => id !== '');
      if (ids.some((id) => ids.indexOf(id) !== ids.lastIndexOf(id))) {
        return { noDuplicates: {} };
      }
      return null;
    };
  }

  private async navigateToMeetingList(): Promise<unknown> {
    const meetingTypeListLink = await this.meetingTypeListViewPage$.pipe(take(1)).toPromise();
    return this.router.navigate([meetingTypeListLink]);
  }
  setAvailability(meetingTypeId: string): void {
    this.savingAvailabilityForm = true;
    const data = this.storeService.dataSubject$$.value;
    const dayEnabled = data?.availability;
    const dayRanges = data?.timeRanges;
    const availability: DailyAvailability = { days: new Map() };
    if (dayEnabled !== undefined) {
      dayEnabled.forEach((available, day) => {
        if (available) {
          availability.days.set(day, dayRanges.get(day));
        }
      });

      this.storeService
        .setAvailability(availability, this.timezone, meetingTypeId, this.hostid)
        .then(() => {
          this.scheduleSettingsViewService.resetStateStorage();
          this.alerts.openSuccessSnack('MEETING_SCHEDULER.SCHEDULE_SETTINGS.ALERTS.AVAILABILITY_SAVED');
        })
        .catch(() => {
          this.alerts.openErrorSnack('MEETING_SCHEDULER.SCHEDULE_SETTINGS.ALERTS.AVAILABILITY_SAVED_ERROR');
        });
    }
  }

  private scrollToAvailabilityPanel(): void {
    const element = document.querySelector('[data-testid="availability-selection-panel"]');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  protected readonly TeamEventMeetingType = TeamEventMeetingType;

  private async getMeetingType(formValue: TeamEventMeetingType): Promise<TeamEventMeetingType> {
    try {
      // Skip the initial startWith(false) and get the actual session detection result
      const isSessionEvent = await this.isSessionEventType$
        .pipe(
          skip(1), // Skip the startWith(false) initial emission
          take(1), // Take the first real emission from loadSessionCalendar
        )
        .toPromise();
      return isSessionEvent ? TeamEventMeetingType.MULTI_HOST : formValue;
    } catch (error) {
      console.error('Error determining meeting type:', error);
      return formValue;
    }
  }

  private updateDateRangeValidators(): void {
    const controls = this.form.controls;
    const dateRangeType = controls.dateRangeType.value;
    const relativeValue = controls.relativeValue;
    const relativeUnit = controls.relativeUnit;
    const startDate = controls.startDate;
    const endDate = controls.endDate;

    // Clear all validators first
    relativeValue.clearValidators();
    relativeUnit.clearValidators();
    startDate.clearValidators();
    endDate.clearValidators();

    if (dateRangeType === DateRangeType.RELATIVE) {
      // Relative mode: enable relative controls, disable date controls
      relativeValue.setValidators([Validators.required, Validators.min(1)]);
      relativeUnit.setValidators([Validators.required]);
      relativeValue.enable();
      relativeUnit.enable();
      startDate.disable();
      endDate.disable();
      // Clear custom date range values when switching to relative
      startDate.setValue(null);
      endDate.setValue(null);
      // Remove form-level validator
      this.form.clearValidators();
    } else {
      // Custom mode: enable date controls, disable relative controls
      startDate.setValidators([Validators.required]);
      endDate.setValidators([Validators.required]);
      startDate.enable();
      endDate.enable();
      relativeValue.disable();
      relativeUnit.disable();
      // Clear relative values when switching to custom
      relativeValue.setValue(null);
      relativeUnit.setValue(null);
      // Add custom validator to ensure end date is after start date
      this.form.setValidators(this.dateRangeValidator);
    }

    // Update validity
    relativeValue.updateValueAndValidity();
    relativeUnit.updateValueAndValidity();
    startDate.updateValueAndValidity();
    endDate.updateValueAndValidity();
  }

  // Initialize date range form
  private initializeDateRangeForm(): void {
    const controls = this.form.controls;
    controls.dateRangeType.setValue(this.dateRangeConfig.dateRangeType);
    controls.dateRangeType.updateValueAndValidity();

    if (this.dateRangeConfig.dateRangeType === DateRangeType.RELATIVE && this.dateRangeConfig.relativeDateRange) {
      if (!this.dateRangeConfig.relativeDateRange.unit) {
        this.dateRangeConfig.relativeDateRange.unit = RelativeTimeUnit.DAYS;
      }

      controls.relativeValue.setValue(this.dateRangeConfig.relativeDateRange.value);
      controls.relativeValue.updateValueAndValidity();
      controls.relativeUnit.setValue(this.dateRangeConfig.relativeDateRange.unit);
      controls.relativeUnit.updateValueAndValidity();
    } else if (this.dateRangeConfig.dateRangeType === DateRangeType.CUSTOM && this.dateRangeConfig.customDateRange) {
      controls.startDate.setValue(this.dateRangeConfig.customDateRange.start);
      controls.startDate.updateValueAndValidity();
      controls.endDate.setValue(this.dateRangeConfig.customDateRange.end);
      controls.endDate.updateValueAndValidity();
    }

    // Update validators based on the type
    this.updateDateRangeValidators();

    // Mark form as pristine after initialization
    this.form.markAsPristine();

    // Trigger change detection to ensure UI updates
    this.cdr.detectChanges();
  }

  private setToEndOfDay(date: Date | null): Date | null {
    if (!date) return null;

    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);
    return endOfDay;
  }

  private buildDateRangeConfig(): EventTypeDateRangeInterface {
    const controls = this.form.controls;
    const dateRangeType = controls.dateRangeType.value;

    if (dateRangeType === DateRangeType.RELATIVE) {
      return {
        dateRangeType: DateRangeType.RELATIVE,
        relativeDateRange: {
          value: controls.relativeValue.value,
          unit: controls.relativeUnit.value,
        },
        customDateRange: null,
      };
    } else {
      return {
        dateRangeType: DateRangeType.CUSTOM,
        relativeDateRange: null,
        customDateRange: {
          start: controls.startDate.value,
          end: this.setToEndOfDay(controls.endDate.value),
        },
      };
    }
  }

  private buildHostOrderConfig(radioChoice: TeamEventMeetingType): Array<{ userId: string; hostOrder: number }> | null {
    if (radioChoice === TeamEventMeetingType.PRIORITY_ROUND_ROBIN && this.selectedTeamMembers?.length > 0) {
      return this.selectedTeamMembers.map((teamMember, index) => ({
        userId: teamMember.userId,
        hostOrder: index + 1, // Ensure this is always a number
      }));
    }
    return null;
  }

  private applyHostOrderToSelectedMembers(hostOrderDetails: Array<{ userId: string; hostOrder: any }>): void {
    if (!hostOrderDetails || !this.selectedTeamMembers) {
      return;
    }

    // Sort the selectedTeamMembers based on the order from hostOrderDetails
    this.selectedTeamMembers.sort((a, b) => {
      const orderA = parseInt(hostOrderDetails.find((h) => h.userId === a.userId)?.hostOrder) || 999;
      const orderB = parseInt(hostOrderDetails.find((h) => h.userId === b.userId)?.hostOrder) || 999;
      return orderA - orderB;
    });
    this.selectedTeamMembersSubject.next(this.selectedTeamMembers);

    // Update hostUserIds to match the new order
    this.hostUserIds = this.selectedTeamMembers.map((member) => member.userId);
  }

  private dateRangeValidator(form: UntypedFormGroup): { [key: string]: boolean } | null {
    const startDate = form.get('startDate');
    const endDate = form.get('endDate');

    if (startDate && endDate && startDate.value && endDate.value) {
      const start = new Date(startDate.value);
      const end = new Date(endDate.value);

      // Reset time to compare only dates
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);

      if (start >= end) {
        return { dateRangeInvalid: true };
      }
    }
    return null;
  }

  // Methods for schedule meeting dialog functionality
  onDateChange(date: Date): void {
    // Only clear the time field if the date has actually changed
    const isDateChanged = !this.selectedDate || this.selectedDate.getTime() !== date.getTime();

    this.selectedDate = date;
    this.startDateControl?.setValue(date);

    // Only clear selected time if the date actually changed
    if (isDateChanged) {
      this.selectedTimeControl?.setValue('');
    }
    // Note: Calendar component handles updating available time slots via availableTimeSlotsChange event
  }

  onAvailableTimeSlotsChange(timeSlots: string[]): void {
    this.availableTimeSlots = timeSlots;
  }

  private clearDateAndTimeSelection(): void {
    this.selectedDate = null;
    this.selectedTimeControl?.setValue('');
    this.availableTimeSlots = [];
  }

  initializeScheduleCalendar(): void {
    const meetingTypeId = this.meetingId;
    const timezone = this.hostPreferences?.timezone?.id;

    // Check if calendar config already exists and matches current requirements
    const newCalendarConfig = {
      calendarId: this.calendarId,
      meetingTypeId: meetingTypeId,
      timezone: timezone,
      userID: '',
      hasCustomDateRange: false,
      customDateRangeStart: null,
      customDateRangeEnd: null,
      eventTypeEndDateInPast: false,
    };

    // Only reset date and time slots if this is a new calendar configuration
    const isNewConfiguration =
      !this.calendarConfig ||
      this.calendarConfig.calendarId !== newCalendarConfig.calendarId ||
      this.calendarConfig.meetingTypeId !== newCalendarConfig.meetingTypeId ||
      this.calendarConfig.timezone !== newCalendarConfig.timezone;

    this.calendarConfig = newCalendarConfig;

    // Only initialize/clear if this is truly a new configuration
    if (isNewConfiguration) {
      this.availableTimeSlots = [];
      this.selectedDate = null;
    }

    // Remove any date restrictions from form controls
    const startDateControl = this.startDateControl;
    if (startDateControl) {
      startDateControl.clearValidators();
      startDateControl.updateValueAndValidity();
    }

    const selectedTimeControl = this.selectedTimeControl;
    if (selectedTimeControl) {
      selectedTimeControl.clearValidators();
      selectedTimeControl.updateValueAndValidity();
    }
  }

  // Priority Assignment drag and drop methods
  onTeamMemberPriorityDrop(event: CdkDragDrop<HostUser[]>): void {
    if (event.previousIndex !== event.currentIndex) {
      moveItemInArray(this.selectedTeamMembers, event.previousIndex, event.currentIndex);
      this.selectedTeamMembersSubject.next(this.selectedTeamMembers);
      // Update hostUserIds to match the new order
      this.hostUserIds = this.selectedTeamMembers.map((member) => member.userId);
      // Trigger change detection to update priority numbers
      this.cdr.detectChanges();
    }
  }

  onTeamMemberPriorityDelete(index: number): void {
    if (index >= 0 && index < this.selectedTeamMembers.length) {
      this.selectedTeamMembers.splice(index, 1);
      this.selectedTeamMembersSubject.next(this.selectedTeamMembers);
      // Update hostUserIds to match the new array
      this.hostUserIds = this.selectedTeamMembers.map((member) => member.userId);
      // Update the team member control to reflect the change
      this.teamMemberCtrl.markAsTouched();
      this.cdr.detectChanges();
    }
  }

  trackByTeamMemberId(index: number, item: HostUser): string {
    return item.userId || item.displayName || index.toString();
  }
}
